/**
 * BudgetDialog — Batch #14.321 / #14.325 : budget par compte de résultat pour l'exercice contenant la date « Du ».
 * Mode annuel (montant réparti au prorata des mois) ou mensuel (12 montants par compte — saisonnalité) ; un compte peut être
 * en mensuel pendant que les autres restent en annuel. L'état des résultats affiche ensuite Réel · Budget · Écart.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, Rows3 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Modal, errMsg, inputCls, money, useGlBase } from "../_shared";

const num = (v) => { const n = parseFloat(String(v ?? "").replace(/\s/g, "").replace(",", ".")); return Number.isFinite(n) ? n : 0; };
const MONTH_SHORT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const cellCls = "w-full border border-slate-200 rounded px-1 py-1 text-[11px] font-mono text-right bg-white focus:outline-none focus:ring-1 focus:ring-emerald-300";

function AnnualSection({ title, rows, amounts, monthly, setAmount, toMonthly }) {
  const total = rows.reduce((s, a) => s + (monthly[a.id] ? monthly[a.id].reduce((x, v) => x + num(v), 0) : num(amounts[a.id] ?? 0)), 0);
  return (
    <div className="space-y-1" data-testid={`budget-section-${title === "Revenus" ? "revenues" : "expenses"}`}>
      <div className="flex items-center justify-between"><h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{title}</h4><span className="text-xs font-semibold text-slate-700">{money(total)} / an</span></div>
      {rows.length === 0 && <p className="text-xs text-slate-400">Aucun compte de ce type dans le plan comptable.</p>}
      {rows.map((a) => (
        <div key={a.id} className="flex items-center gap-2">
          <span className="font-mono text-xs w-12 text-slate-500">{a.number}</span>
          <span className="flex-1 text-sm truncate" title={a.name}>{a.name}</span>
          {monthly[a.id]
            ? <button onClick={() => toMonthly(a.id)} className="w-36 shrink-0 text-right text-xs font-mono text-emerald-700 underline" title="Réparti mois par mois — cliquer pour voir" data-testid={`budget-monthly-${a.number}`}>{money(monthly[a.id].reduce((x, v) => x + num(v), 0))} · 12 mois</button>
            : <div className="w-36 shrink-0"><input type="text" inputMode="decimal" className={`${inputCls} text-right font-mono`} value={amounts[a.id] ?? ""} placeholder="0,00" onChange={(e) => setAmount(a.id, e.target.value)} data-testid={`budget-amount-${a.number}`} /></div>}
          <button onClick={() => toMonthly(a.id)} className="text-slate-400 hover:text-emerald-700" title="Répartir mois par mois" data-testid={`budget-to-monthly-${a.number}`}><CalendarDays className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

function MonthlyGrid({ rows, months, monthly, amounts, setMonth, toAnnual, spread }) {
  return (
    <div className="overflow-x-auto" data-testid="budget-monthly-grid">
      <table className="text-xs min-w-[900px]">
        <thead><tr className="text-[10px] text-slate-500"><th className="text-left px-1 w-44">Compte</th>{months.map((m) => <th key={m} className="px-1 text-right">{MONTH_SHORT[Number(m.slice(5, 7)) - 1]}<br /><span className="text-slate-300">{m.slice(0, 4)}</span></th>)}<th className="px-1 text-right">Total</th><th /></tr></thead>
        <tbody>
          {rows.map((a) => {
            const vals = monthly[a.id] || Array(12).fill("");
            const tot = vals.reduce((s, v) => s + num(v), 0);
            return (
              <tr key={a.id} className="border-t">
                <td className="px-1 py-1 truncate max-w-[11rem]" title={a.name}><span className="font-mono text-slate-500 mr-1">{a.number}</span>{a.name}</td>
                {vals.map((v, i) => <td key={i} className="px-0.5 py-0.5"><input type="text" inputMode="decimal" className={cellCls} value={v} placeholder="0" onChange={(e) => setMonth(a.id, i, e.target.value)} data-testid={`budget-cell-${a.number}-${i}`} /></td>)}
                <td className="px-1 text-right font-mono font-semibold whitespace-nowrap" data-testid={`budget-row-total-${a.number}`}>{money(tot)}</td>
                <td className="px-1 whitespace-nowrap">
                  <button onClick={() => spread(a.id)} className="text-[10px] text-emerald-700 underline mr-2" title={`Répartir ${money(num(amounts[a.id] ?? tot))} également sur 12 mois`} data-testid={`budget-spread-${a.number}`}>÷ 12</button>
                  <button onClick={() => toAnnual(a.id)} className="text-[10px] text-slate-500 underline" title="Revenir au montant annuel" data-testid={`budget-to-annual-${a.number}`}>annuel</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function BudgetDialog({ dateFrom, onClose, onSaved }) {
  const base = useGlBase();
  const [data, setData] = useState(null);
  const [amounts, setAmounts] = useState({});
  const [monthly, setMonthly] = useState({});
  const [view, setView] = useState("annual");
  const [busy, setBusy] = useState(false);
  const touched = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get(`${base}/reports/income-statement/budget`, { params: { date_from: dateFrom || undefined } })
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        if (!touched.current) {
          setAmounts(Object.fromEntries(r.data.accounts.filter((a) => a.amount && !a.monthly).map((a) => [a.id, String(a.amount)])));
          setMonthly(Object.fromEntries(r.data.accounts.filter((a) => a.monthly).map((a) => [a.id, a.monthly.map((v) => (v ? String(v) : ""))])));
        }
      })
      .catch((e) => { if (alive) toast.error(errMsg(e, "Budget indisponible")); });
    return () => { alive = false; };
  }, [base, dateFrom]);

  const rev = useMemo(() => (data?.accounts || []).filter((a) => a.type === "revenue"), [data]);
  const exp = useMemo(() => (data?.accounts || []).filter((a) => a.type === "expense"), [data]);
  const setAmount = (id, v) => { touched.current = true; setAmounts((x) => ({ ...x, [id]: v })); };
  const setMonth = (id, i, v) => { touched.current = true; setMonthly((m) => { const row = [...(m[id] || Array(12).fill(""))]; row[i] = v; return { ...m, [id]: row }; }); };
  const toMonthly = (id) => { touched.current = true; setMonthly((m) => m[id] ? m : { ...m, [id]: Array(12).fill("") }); setView("monthly"); };
  const toAnnual = (id) => { touched.current = true; setMonthly((m) => { const tot = (m[id] || []).reduce((s, v) => s + num(v), 0); const n = { ...m }; delete n[id]; if (tot) setAmounts((x) => ({ ...x, [id]: String(tot) })); return n; }); };
  const spread = (id) => {
    touched.current = true;
    const tot = num(amounts[id]) || (monthly[id] || []).reduce((s, v) => s + num(v), 0);
    if (!tot) return;
    const per = Math.round(tot / 12 * 100) / 100;
    const row = Array(12).fill(String(per)); row[11] = String(Math.round((tot - per * 11) * 100) / 100);
    setMonthly((m) => ({ ...m, [id]: row }));
  };
  const totalOf = (rows) => rows.reduce((s, a) => s + (monthly[a.id] ? monthly[a.id].reduce((x, v) => x + num(v), 0) : num(amounts[a.id] ?? 0)), 0);
  const net = totalOf(rev) - totalOf(exp);
  const monthlyRows = (data?.accounts || []).filter((a) => monthly[a.id]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(amounts).filter(([k]) => !monthly[k]).map(([k, v]) => [k, num(v)]).filter(([, v]) => v));
      const mon = Object.fromEntries(Object.entries(monthly).map(([k, row]) => [k, row.map(num)]).filter(([, row]) => row.some((v) => v)));
      await api.put(`${base}/reports/income-statement/budget`, { amounts: payload, monthly: mon }, { params: { date_from: dateFrom || undefined } });
      const nb = Object.keys(payload).length + Object.keys(mon).length;
      toast.success(nb ? `Budget enregistré (${Object.keys(mon).length} compte(s) mois par mois) — colonnes Budget et Écart ajoutées à l'état des résultats` : "Budget effacé — l'état des résultats revient au format simple");
      onSaved?.(); onClose();
    } catch (e) { toast.error(errMsg(e, "Enregistrement impossible")); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Budget — exercice ${data ? `du ${data.fy_start} au ${data.fy_end}` : "…"}`} onClose={onClose} wide testId="budget-dialog"
      footer={<><button onClick={onClose} className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="budget-cancel">Annuler</button>
        <button onClick={save} disabled={busy || !data} className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="budget-save">{busy ? "Enregistrement…" : "Enregistrer le budget"}</button></>}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-slate-500 flex-1 min-w-[16rem]">Montant <strong>annuel</strong> réparti au prorata des mois affichés, ou <strong>mois par mois</strong> (icône calendrier) pour les comptes saisonniers : l'état des résultats compare alors chaque mois à son budget réel. La prévision de trésorerie peut aussi s'en servir.</p>
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
          <button onClick={() => setView("annual")} className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${view === "annual" ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`} data-testid="budget-view-annual"><Rows3 className="w-3.5 h-3.5" /> Annuel</button>
          <button onClick={() => setView("monthly")} className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${view === "monthly" ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`} data-testid="budget-view-monthly"><CalendarDays className="w-3.5 h-3.5" /> Mensuel ({monthlyRows.length})</button>
        </div>
      </div>
      {!data && <p className="text-sm text-slate-400 py-6 text-center">Chargement…</p>}
      {data && view === "annual" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-h-[55vh] overflow-y-auto pr-1">
          <AnnualSection title="Revenus" rows={rev} amounts={amounts} monthly={monthly} setAmount={setAmount} toMonthly={toMonthly} />
          <AnnualSection title="Dépenses" rows={exp} amounts={amounts} monthly={monthly} setAmount={setAmount} toMonthly={toMonthly} />
        </div>
      )}
      {data && view === "monthly" && (
        <div className="max-h-[55vh] overflow-y-auto">
          {monthlyRows.length === 0 && <p className="text-xs text-slate-500 py-4 text-center">Aucun compte en mode mensuel. Dans la vue Annuel, cliquez l'icône calendrier d'un compte pour saisir ses 12 mois.</p>}
          {monthlyRows.length > 0 && <MonthlyGrid rows={monthlyRows} months={data.months} monthly={monthly} amounts={amounts} setMonth={setMonth} toAnnual={toAnnual} spread={spread} />}
          <div className="mt-2"><Btn tone="slate" icon={Rows3} onClick={() => setView("annual")} testId="budget-back-annual">Retour à la vue annuelle</Btn></div>
        </div>
      )}
      {data && <div className={`rounded-lg px-3 py-2 text-sm font-semibold flex justify-between ${net >= 0 ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`} data-testid="budget-net">
        <span>Résultat budgété de l'exercice</span><span className="font-mono">{money(net)}</span></div>}
    </Modal>
  );
}
